import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PageSpeedMetrics {
  score: number;
  grade: string;
  loading: boolean;
  error: string | null;
}

export function usePageSpeedMetrics(websiteId: string, enabled: boolean = true): PageSpeedMetrics {
  const [metrics, setMetrics] = useState<PageSpeedMetrics>({
    score: 0,
    grade: 'N/A',
    loading: true,
    error: null
  });

  useEffect(() => {
    if (!websiteId || !enabled) {
      setMetrics(prev => ({ ...prev, loading: false }));
      return;
    }

    const fetchPageSpeedMetrics = async () => {
      try {
        setMetrics(prev => ({ ...prev, loading: true, error: null }));

        // Get the latest SEO score data
        const { data: seoData, error: seoError } = await supabase
          .from('seo_scores')
          .select('breakdown, last_updated')
          .eq('website_id', websiteId)
          .order('last_updated', { ascending: false })
          .limit(1)
          .single();

        if (seoError) throw seoError;

        if (seoData && seoData.breakdown) {
          // Extract technical score which includes page speed factors
          const technicalScore = seoData.breakdown.technical?.score || 0;
          
          // Convert technical score to a page speed score (0-100)
          // Technical score already includes page speed factors from our SEO calculator
          const pageSpeedScore = Math.round(technicalScore);
          
          // Convert score to grade
          const getGrade = (score: number) => {
            if (score >= 90) return 'A+';
            if (score >= 80) return 'A';
            if (score >= 70) return 'B';
            if (score >= 60) return 'C';
            if (score >= 50) return 'D';
            return 'F';
          };

          setMetrics({
            score: pageSpeedScore,
            grade: getGrade(pageSpeedScore),
            loading: false,
            error: null
          });
        } else {
          setMetrics({
            score: 0,
            grade: 'N/A',
            loading: false,
            error: null
          });
        }
      } catch (error) {
        console.error('Error fetching page speed metrics:', error);
        setMetrics(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to fetch page speed metrics'
        }));
      }
    };

    fetchPageSpeedMetrics();
  }, [websiteId, enabled]);

  return metrics;
}


